/* eslint-disable no-console */

import type { AxiosInstance, AxiosResponse, CreateAxiosDefaults } from 'axios';
import type { PluginObject } from 'vue';

import axios, { AxiosError } from 'axios';

import { decrementRequestCounter, incrementRequestCounter } from '@/composables/global.request-counter';

const HttpPlugin: PluginObject<{ instance?: AxiosInstance, afterInstall: (instance: AxiosInstance) => void }> = {
  install(app, options) {
    if (this.installed)
      return;

    let http = options?.instance as AxiosInstance;
    if (!http) {
      const baseConfig: CreateAxiosDefaults = {
        baseURL: __API_URL__,
        method: 'POST',
        withCredentials: true,
        headers: {
          'post': {
            'Content-Type': 'application/json',
          },
          'get': {
            // не желаем хранить кэш для get запросов
            'Cache-Control': 'no-store',
          },
          'X-App-Build': JSON.stringify(`${__BUILD_UTC_AT__}-${import.meta.env.MODE}`),
        },
      };

      if (import.meta.env.DEV) {
        (baseConfig.headers as Record<string, any>)['X-App-Build'] = '?0';
        (baseConfig.headers as Record<string, any>)['X-Dev-App'] = 'true';
      }

      http = axios.create(baseConfig);

      http.interceptors.request.use(
        (request) => {
          if (import.meta.env.DEV) {
            console.groupCollapsed('Request', `${request.baseURL}${request.url}`);
            console.info(request);
            console.groupEnd();
          }

          request.globalLoader && incrementRequestCounter();

          if (typeof request.shouldRetry === 'function') {
            request.maxRetries ??= 3;
          }

          return request;
        },
        (error: AxiosError) => {
          if (import.meta.env.DEV) {
            console.groupCollapsed('Request Error', `${error?.config?.baseURL}${error?.config?.url}`);
            console.info(error);
            console.groupEnd();
          }

          error.config?.globalLoader && decrementRequestCounter();

          return Promise.reject(error);
        }
      );

      http.interceptors.response.use(
        (response) => {
          if (import.meta.env.DEV) {
            console.groupCollapsed('Response', `${response.config.baseURL}${response.config.url}`);
            console.info(response);
            console.groupEnd();
          }

          response.config.globalLoader && decrementRequestCounter();

          // FIXME: rename field response to apiData / apiResponse or smth

          if (response.data) {
            if (response.data.status === 'ok' || response.data.status === 'redirect') {
              return Object.assign(
                {
                  response: response.data.response,
                  ok: true,
                  err: false,
                },
                response
              );
            }
          }

          return Promise.reject(
            Object.assign(
              {
                response: response.data,
                ok: false,
                err: true,
              },
              response
            )
          );
        },
        (error) => {
          if (import.meta.env.DEV) {
            console.groupCollapsed('Response Error', `${error?.config?.baseURL}${error?.config?.url}`);
            console.info(error);
            console.groupEnd();
          }

          error.config?.globalLoader && decrementRequestCounter();

          return Promise.reject(error);
        }
      );

      const retryFunction = (axiosWrapper: AxiosResponse) => {
        let response: AxiosResponse | undefined,
          error: any;

        if (axiosWrapper instanceof AxiosError || axios.isAxiosError(axiosWrapper)) {
          error = axiosWrapper;
        }
        else {
          response = axiosWrapper;
        }

        if (axiosWrapper.config?.shouldRetry?.(response, error)) {
          if (axiosWrapper.config.retryTimeoutHandle != null) {
            clearTimeout(axiosWrapper.config.retryTimeoutHandle);
            axiosWrapper.config.retryTimeoutHandle = null;
          }

          const currentRetry = (axiosWrapper.config.retries ?? 0) + 1;
          axiosWrapper.config.retries = currentRetry;

          if (currentRetry > axiosWrapper.config.maxRetries!) {
            return Promise.reject(axiosWrapper);
          }

          if (axiosWrapper.config?.signal?.aborted) {
            return Promise.reject(axiosWrapper);
          }

          const retryDelay = axiosWrapper.config.retryDelay ?? 1500;

          return new Promise<AxiosResponse>((resolve) => {
            axiosWrapper.config.retryTimeoutHandle = setTimeout(() => resolve(http(axiosWrapper.config)), currentRetry * retryDelay);
          });
        }

        if (error || (axiosWrapper as unknown as any).err === true) {
          return Promise.reject(axiosWrapper);
        }

        return Promise.resolve(axiosWrapper);
      };

      http.interceptors.response.use(retryFunction, retryFunction);
    }

    app.prototype.$http = app.http = http;

    options?.afterInstall(http);

    this.installed = true;
  },
};

// type ExtendedApiResponse = AxiosResponse & {
//   response: any;
//   ok: boolean;
//   err: boolean;
// };

declare module 'vue/types/vue' {
  interface VueConstructor {
    http: AxiosInstance
  }

  interface Vue {
    $http: AxiosInstance
  }
}

export default HttpPlugin;
